"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { CustomEase } from "gsap/CustomEase";
import { SplitText } from "gsap/SplitText";
import { AfricaTownLogo } from "@/components/AfricaTownLogo";
import { MENU_LINKS, MENU_SECTIONS, type SectionId } from "./sections";

gsap.registerPlugin(CustomEase);
gsap.registerPlugin(SplitText);

const OPEN_CLIP = "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)";
const CLOSED_CLIP = "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)";

interface MenuPanelProps {
  isOpen: boolean;
  onAnimationComplete?: () => void;
}

export function MenuPanel({ isOpen, onAnimationComplete }: MenuPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  const [activeSection, setActiveSection] = useState<SectionId>("idle");
  // Sections play their entrance on mount, so the feature column stays empty
  // until the panel opens — and stays filled until it has finished closing.
  const [isContentMounted, setIsContentMounted] = useState(false);

  useEffect(() => {
    if (isOpen) setIsContentMounted(true);
  }, [isOpen]);

  useGSAP(
    () => {
      const panel = panelRef.current;
      const menu = menuRef.current;
      if (!panel || !menu) return;

      // Nothing to animate on mount: the links and the video start hidden in CSS.
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      const links = gsap.utils.toArray(panel.querySelectorAll(".link"));
      const video = panel.querySelector(".video-wrapper");

      if (isOpen) {
        const tl = gsap.timeline({
          onComplete: () => onAnimationComplete?.(),
        });

        tl.to(menu, {
          clipPath: OPEN_CLIP,
          ease: "hop",
          duration: 0.5,
          onStart: () => {
            gsap.set(menu, { pointerEvents: "all" });
          },
        });

        tl.to(
          links,
          {
            y: 0,
            opacity: 1,
            stagger: 0.1,
            delay: 0.05,
            duration: 1,
            ease: "power3.out",
          },
          "<",
        );

        if (video) {
          tl.to(video, { clipPath: OPEN_CLIP, ease: "hop", duration: 0.5 }, "<");
        }
      } else {
        gsap.to(menu, {
          clipPath: CLOSED_CLIP,
          ease: "hop",
          duration: 0.5,
          onComplete: () => {
            gsap.set(menu, { pointerEvents: "none" });
            setIsContentMounted(false);
            setActiveSection("idle");
            onAnimationComplete?.();
          },
        });

        gsap.set(links, { y: 30, opacity: 0 });
        if (video) gsap.set(video, { clipPath: CLOSED_CLIP });
      }
    },
    { scope: panelRef, dependencies: [isOpen] },
  );

  const ActiveSection = MENU_SECTIONS[activeSection].Component;

  return (
    <div ref={panelRef}>
      <div ref={menuRef} className="menu bg-background">
        {/* Overlays the navbar logo rather than joining the grid, so the two
            line up exactly. Both use the --nav-inset-* pair. */}
        <div className="menu-logo">
          <a href="/" aria-label="AfricaTown home">
            <AfricaTownLogo
              className="h-12 sm:h-16 md:h-20 lg:h-24 w-auto text-text-on-background"
              buildingsColor="var(--color-primary)"
            />
          </a>
        </div>

        <nav className="menu-links text-surface-elevated" aria-label="Menu">
          {MENU_LINKS.map((id) => (
            <div className="link" key={id}>
              <button
                type="button"
                aria-pressed={activeSection === id}
                onClick={() => setActiveSection(id)}
              >
                {MENU_SECTIONS[id].label}
              </button>
            </div>
          ))}
        </nav>

        <div className="menu-media video-wrapper">
          <video autoPlay muted loop>
            <source src="/videos/africatown-seattle-intro.mp4" type="video/mp4" />
          </video>
        </div>

        {/* A fixed grid area: swapping what is inside it cannot reflow the panel. */}
        <div className="menu-feature">
          {isContentMounted && <ActiveSection key={activeSection} />}
        </div>
      </div>
    </div>
  );
}
